import { Fragment, useState, useMemo } from 'react';
export interface SortableColumn<T> {
  key: string;
  label: string;
  width?: string;
  render: (row: T) => React.ReactNode;
  sortValue?: (row: T) => string | number;
  align?: 'left' | 'right' | 'center';
}
interface SortState {
  key: string | null;
  dir: 'asc' | 'desc';
}
interface SortableTableProps<T> {
  columns: SortableColumn<T>[];
  data: T[];
  defaultSort?: {
    key: string;
    dir: 'asc' | 'desc';
  };
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string;
  className?: string;
  renderExpandedRow?: (row: T) => React.ReactNode;
}
function getComparableValue<T>(row: T, col: SortableColumn<T>): string | number {
  if (col.sortValue) return col.sortValue(row);
  const val = (row as Record<string, unknown>)[col.key];
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return val;
  return String(val ?? '');
}
function compareValues(a: string | number, b: string | number, dir: 'asc' | 'desc'): number {
  let result: number;
  if (typeof a === 'string' && typeof b === 'string') {
    result = a.localeCompare(b);
  } else {
    result = (a as number) - (b as number);
  }
  return dir === 'asc' ? result : -result;
}
export function SortableTable<T>({
  columns,
  data,
  defaultSort,
  onRowClick,
  rowKey,
  className = 'tbl',
  renderExpandedRow
}: SortableTableProps<T>) {
  const [sort, setSort] = useState<SortState>({
    key: defaultSort?.key ?? null,
    dir: defaultSort?.dir ?? 'desc'
  });
  const sortedData = useMemo(() => {
    if (!sort.key) return data;
    const col = columns.find(c => c.key === sort.key);
    if (!col) return data;
    return [...data].sort((a, b) => compareValues(getComparableValue(a, col), getComparableValue(b, col), sort.dir));
  }, [data, sort.key, sort.dir, columns]);
  const handleClick = (colKey: string) => {
    setSort(prev => {
      if (prev.key === colKey) {
        if (prev.dir === 'asc') return {
          key: colKey,
          dir: 'desc'
        };
        return {
          key: null,
          dir: 'desc'
        };
      }
      return {
        key: colKey,
        dir: 'asc'
      };
    });
  };
  const sortIndicator = (colKey: string) => {
    const isActive = sort.key === colKey;
    return <span style={{
      marginLeft: 3,
      fontSize: 8,
      color: isActive ? '#fa0f00' : '#c0c5ce'
    }} data-id="element-721" data-mp="2ga13wbru9vv0">
        {isActive ? sort.dir === 'asc' ? '▲' : '▼' : '▲'}
      </span>;
  };
  return <table className={className} data-id="element-722" data-mp="38yge8l00zq00">
      <thead data-id="element-723" data-mp="38zhrmw4ikokw">
        <tr data-id="element-724" data-mp="38zrn2z8bvnxz">
          {columns.map(col => <th key={col.key} style={{
          width: col.width,
          cursor: 'pointer',
          userSelect: 'none',
          textAlign: col.align
        }} onClick={() => handleClick(col.key)} data-id="element-725" data-mp="3dxjkg8rqs29k">
              {col.label}{sortIndicator(col.key)}
            </th>)}
        </tr>
      </thead>
      <tbody data-id="element-726" data-mp="in18g4wkwnp">
        {sortedData.map((row, i) => <Fragment key={rowKey(row) || i} data-id="element-727" data-mp="k7um2fhe9jz" data-mpi={i}>
            <tr onClick={onRowClick ? () => onRowClick(row) : undefined} data-id="element-728" data-mp="1l925mjm6lcpd">
              {columns.map(col => <td key={col.key} style={{
            textAlign: col.align
          }} data-id="element-729" data-mp="1gah50j30or0e">
                  {col.render(row)}
                </td>)}
            </tr>
            {renderExpandedRow?.(row)}
          </Fragment>)}
      </tbody>
    </table>;
}